import moment, { Moment } from "moment";
import { Matcher, isMatch } from "react-day-picker";
import {
  AssertIsAfterDate,
  AssertIsAfterOrEqualDate,
  AssertIsBeforeOrEqualDate,
} from "../utils/assert";
import {
  getTimeStringFromDate,
  getUTCTimeString,
} from "../utils/timeFormatter";
import config from "../config";

const RANGE_OPTIONS = {
  weekly: "week",
  monthly: "month",
  daily: "day",
};

export const addToDateRange = (
  selectedDate: Date,
  dateRange: { from?: Date; to?: Date },
  allowOneDayRange?: boolean,
  range?: "weekly" | "monthly" | "daily"
) => {
  const interval = RANGE_OPTIONS[range ?? "daily"] as moment.unitOfTime.StartOf;

  // first selection hasn't been made or need to reset after both `from` and `to` are picked
  if (!dateRange.from || (dateRange.from && dateRange.to)) {
    return { from: moment(selectedDate).startOf(interval).toDate() };
  }

  // there exist a `from` but not a `to`, if a valid `to` date given then set `to`
  // else move `from` back to the selectedDate

  const validator = allowOneDayRange
    ? AssertIsAfterOrEqualDate
    : AssertIsAfterDate;

  return validator(selectedDate, dateRange.from, "day")
    ? {
        from: dateRange.from,
        to: moment(selectedDate).endOf(interval).toDate(),
      }
    : { from: moment(selectedDate).startOf(interval).toDate() };
};

export const getMoment = (date: Date, utc?: boolean) => {
  if (utc) {
    return moment.utc(date);
  }

  return moment(date);
};

export const getFormatDate = (
  dateFormat: string,
  date?: Date,
  utc?: boolean
) => {
  if (!date) {
    return undefined;
  }

  if (utc) {
    return getUTCTimeString(date, dateFormat);
  }

  return getTimeStringFromDate(date, dateFormat);
};

export const isValidDateInput = (
  date: Date | Moment,
  activeKey: "from" | "to",
  otherDate: Date | string
) => {
  const REASONABLE_START_DATE = moment(
    "2010-01-01",
    config.DATE_FORMAT
  ).toDate();
  const REASONABLE_END_DATE = moment("2038-12-31", config.DATE_FORMAT).toDate();

  if (activeKey === "to" && !AssertIsAfterOrEqualDate(date, otherDate, "day")) {
    return false;
  }

  if (
    activeKey === "from" &&
    !AssertIsBeforeOrEqualDate(date, otherDate, "day")
  ) {
    return false;
  }

  return (
    AssertIsAfterOrEqualDate(date, REASONABLE_START_DATE, "day") &&
    AssertIsBeforeOrEqualDate(date, REASONABLE_END_DATE, "day")
  );
};

export const getDefaultCustomDateRange = (
  currentDate: Date,
  isUTC?: boolean
) => {
  return [
    {
      label: "Last 7 days",
      from: getMoment(currentDate, isUTC).subtract(6, "days").toDate(),
      to: currentDate,
    },
    {
      label: "Last 30 days",
      from: getMoment(currentDate, isUTC).subtract(29, "days").toDate(),
      to: currentDate,
    },
    {
      label: "Last Month",
      from: getMoment(currentDate, isUTC)
        .subtract(1, "month")
        .startOf("month")
        .toDate(),
      to: getMoment(currentDate, isUTC)
        .subtract(1, "month")
        .endOf("month")
        .toDate(),
    },
    {
      label: "Current Month",
      from: getMoment(currentDate, isUTC).startOf("month").toDate(),
      to: getMoment(currentDate, isUTC).endOf("month").toDate(),
    },
    {
      label: "Last 60 days",
      from: getMoment(currentDate, isUTC).subtract(59, "days").toDate(),
      to: currentDate,
    },
    {
      label: "Last 90 days",
      from: getMoment(currentDate, isUTC).subtract(89, "days").toDate(),
      to: currentDate,
    },
  ];
};

export const isDisabledDay = (
  date: Date,
  disabledDays?: Matcher | Matcher[]
) => {
  if (!disabledDays) return false;

  const arrayOfMatchers = Array.isArray(disabledDays)
    ? disabledDays
    : [disabledDays];

  return isMatch(date, arrayOfMatchers);
};

export const getActiveCustomDateRange = (
  dateRange: Array<{
    label: string;
    from: Date;
    to: Date;
  }>,
  disabledDays?: Matcher | Matcher[]
) => {
  return dateRange.filter(
    (dateRange) =>
      !isDisabledDay(dateRange.from, disabledDays) &&
      !isDisabledDay(dateRange.to, disabledDays)
  );
};

export const getHoverRangePreview = (
  startDate: Date,
  range?: "weekly" | "monthly" | "daily",
  allowMultipleRange?: boolean,
  currentRange?: { from?: Date; to?: Date }
) => {
  if (!range) return undefined;

  const hoverInterval = RANGE_OPTIONS[range] as moment.unitOfTime.StartOf;

  let from;
  let to;

  if (!currentRange?.from) {
    // first selection hasn't been made, nothing to hover
    if (range === "daily") {
      return;
    } else {
      // preview the month/week being selected
      from = moment(startDate).startOf(hoverInterval).toDate();
      to = moment(startDate).endOf(hoverInterval).toDate();
    }
  } else {
    if (range === "daily") {
      const otherDate = currentRange?.from;

      // if they're selecting a date other than before, don't show a preview
      if (otherDate && AssertIsAfterOrEqualDate(startDate, otherDate)) {
        from = moment(otherDate).startOf(hoverInterval).toDate();
        to = moment(startDate).endOf(hoverInterval).toDate();
      }
    } else {
      // if allowing for multiple ranges, start from given `from` else only give
      // the current interval
      const otherDate = allowMultipleRange ? currentRange?.from : startDate;

      from = moment(otherDate).startOf(hoverInterval).toDate();
      to = moment(startDate).endOf(hoverInterval).toDate();
    }
  }

  return {
    // hoverRange does not include from and to
    hoverRange: { after: from, before: to },
    hoverRangeStart: from,
    hoverRangeEnd: to,
  };
};
